"use client";
import React from "react";
import styled from "styled-components";

// 🎯 TOGGLE THIS: Set to false to hide placeholders and see real ads in development
const SHOW_AD_PLACEHOLDERS = false;

const DevAdContainer = styled.div<{
  $isProduction: boolean;
  $order?: number;
  $hidden?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ $order }) => $order !== undefined && `order: ${$order};`}
  ${({ $hidden }) => $hidden && `display: none;`}

  margin: 1rem 0;
  border: 2px dashed #007bff;
  background: linear-gradient(45deg, #f8f9fa 25%, transparent 25%),
    linear-gradient(-45deg, #f8f9fa 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f8f9fa 75%),
    linear-gradient(-45deg, transparent 75%, #f8f9fa 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  min-height: 90px;
  position: relative;
  border-radius: 8px;

  /* Development placeholder */
  &::before {
    content: "📢 " attr(data-slot-id) " Ad Slot";
    color: #007bff;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  /* Ad content styling */
  > div {
    min-height: 90px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    /* When ad loads, hide placeholder */
    &:has(iframe),
    &:has(ins),
    &:has(.ad-content) {
      & ~ *::before,
      + *::before {
        display: none;
      }
    }
  }

  /* In production, completely hide empty ad slots */
  ${({ $isProduction }) =>
    $isProduction
      ? `
    > div:not(:has(iframe)):not(:has(ins)):not(:has(.ad-content)) {
      display: none !important;
    }
    
    &:has(> div:not(:has(iframe)):not(:has(ins)):not(:has(.ad-content))) {
      display: none !important;
    }
  `
      : ""}
`;

const AdContainer = styled.div<{ $order?: number; $hidden?: boolean }>`
  ${({ $order }) => $order !== undefined && `order: ${$order};`}
  ${({ $hidden }) => $hidden && `display: none;`}

  & > div {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  /* Hide entire container when the inner div has no child elements */
  /* This should work in Chrome which supports :has() */
  &:not(:has(> div *)) {
    display: none !important;
  }
`;

interface AdSlotProps {
  slotId: string;
  className?: string;
  order?: number;
  hidden?: boolean;
  /** Environment for ad display logic. Defaults to "production" (no dev placeholders). */
  currentEnv?: "development" | "staging" | "production";
}

export default function AdSlot({
  slotId,
  className,
  order,
  hidden,
  currentEnv = "production",
}: AdSlotProps) {
  const isProduction = currentEnv === "production";

  // Show placeholders only in local development, not in staging or production
  const shouldShowPlaceholder =
    SHOW_AD_PLACEHOLDERS && currentEnv === "development";

  if (!shouldShowPlaceholder) {
    // No placeholder styling - just the basic ad container
    return (
      <AdContainer className={className} $order={order} $hidden={hidden}>
        <div id={slotId}></div>
      </AdContainer>
    );
  }

  return (
    <DevAdContainer
      className={className}
      data-slot-id={slotId}
      $isProduction={isProduction}
      $order={order}
      $hidden={hidden}
    >
      <div id={slotId}></div>
    </DevAdContainer>
  );
}
